import React, { FormEvent, useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import styles from './EmailUnsubscribe.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';

const FORM_FIELD_IDS = {
  EMAIL: 'email',
} as const;

export interface EmailUnsubscribeProps {
  //subscriptionId?: string;
  formTitle: React.ReactNode;
  formSubheading: React.ReactNode;
  emailLabel: React.ReactNode;
  emailPlaceholder: string;
  ctaButtonText: React.ReactNode;
  requiredText: React.ReactNode;
}

interface UnsubscribeResponse {
  success?: boolean;
  error?: string;
  error_description?: string;
}

export default function EmailUnsubscribe(props: EmailUnsubscribeProps) {
  const {
    //subscriptionId: propSubscriptionId,
    formTitle,
    formSubheading,
    emailLabel,
    emailPlaceholder,
    ctaButtonText,
    requiredText,
  } = props;

  const { getDictionaryValue } = useDictionary();
  const { data: session } = useSessionSafe();
  const router = useRouter();
  const { setFieldError, clearFieldError, clearAllErrors, getFieldError } = useFormErrors();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Get email from URL query parameters if available
  useEffect(() => {
    if (router.isReady) {
      const { email: emailParam } = router.query;

      // Prefill email from URL parameter if available
      if (emailParam && typeof emailParam === 'string') {
        setEmail(emailParam);
      }
    }
  }, [router.isReady, router.query]);

  // Prefill email if user is signed in (only if not already set from URL)
  useEffect(() => {
    if (session?.user?.email && !email) {
      setEmail(session.user.email);
    }
  }, [session, email]);

  /**
   * Validates the email field
   */
  const validateEmail = useCallback(
    (emailValue: string): boolean => {
      if (!emailValue.trim()) {
        const requiredMessage =
          getDictionaryValue('Components.EmailUnsubscribe.EmailRequired') ||
          'Please enter your email address.';
        setFieldError(FORM_FIELD_IDS.EMAIL, requiredMessage);
        return false;
      }

      // Email pattern validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue)) {
        const invalidMessage =
          getDictionaryValue('Components.EmailUnsubscribe.InvalidEmail') ||
          'This email is not valid. Please re-enter your email address and try again.';
        setFieldError(FORM_FIELD_IDS.EMAIL, invalidMessage);
        return false;
      }

      clearFieldError(FORM_FIELD_IDS.EMAIL);
      return true;
    },
    [getDictionaryValue, setFieldError, clearFieldError]
  );

  /**
   * Handles email input changes - clears error while typing
   */
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const emailValue = e.target.value;
      setEmail(emailValue);

      // Clear the error when user starts typing (don't revalidate until submit)
      if (hasAttemptedSubmit) {
        clearFieldError(FORM_FIELD_IDS.EMAIL);
        setSubmitError('');
      }
    },
    [hasAttemptedSubmit, clearFieldError]
  );

  /**
   * Handles email paste events - clears error when pasting
   */
  const handleEmailPaste = useCallback(
    (_e: React.ClipboardEvent<HTMLInputElement>) => {
      setTimeout(() => {
        if (hasAttemptedSubmit) {
          clearFieldError(FORM_FIELD_IDS.EMAIL);
          setSubmitError('');
        }
      }, 0);
    },
    [hasAttemptedSubmit, clearFieldError]
  );

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setHasAttemptedSubmit(true);

    // Clear previous errors and validate
    clearAllErrors();
    if (!validateEmail(email)) {
      const mainHeading = getDictionaryValue(
        'Components.EmailUnsubscribe.InvalidEmailMainHeading',
        'Invalid Email'
      );
      const subHeading = getDictionaryValue(
        'Components.EmailUnsubscribe.InvalidEmailSubHeading',
        'Please check that you have filled in all required fields'
      );

      const emptyFieldsErrorMessage = `${mainHeading} | ${subHeading}`;
      setSubmitError(emptyFieldsErrorMessage);

      // GTM tracking for validation error
      pushToDataLayer({
        event: 'unsubscribe_form_error',
        error_type: 'validation',
        error_message: 'Invalid or missing email',
      });

      return;
    }

    setIsSubmitting(true);

    // TODO: Remove the commented code once suppression API is confirmed to work.
    // if (!propSubscriptionId) {
    //   const errorHeading = getDictionaryValue(
    //     'Components.EmailUnsubscribe.ConfigErrorHeading',
    //     'Configuration Error'
    //   );
    //   const errorSubHeading = getDictionaryValue(
    //     'Components.EmailUnsubscribe.ConfigErrorSubHeading',
    //     'Subscription settings are missing for this site.'
    //   );
    //   setSubmitError(`${errorHeading} | ${errorSubHeading}`);
    //   setIsSubmitting(false);
    //   return;
    // }

    try {
      const response = await fetch('/api/suppress-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'Email',
        }),
      });
      const result: UnsubscribeResponse = await response.json();

      if (!response.ok || result.error) {
        // Handle error response
        const errorHeading = getDictionaryValue(
          'Components.EmailUnsubscribe.SystemErrorHeading',
          'System Error'
        );
        const errorSubHeading = getDictionaryValue(
          'Components.EmailUnsubscribe.SystemErrorSubHeading',
          'An error occurred while processing your request. Please try again later.'
        );
        const genericErrorMessage = `${errorHeading} | ${errorSubHeading}`;
        setSubmitError(genericErrorMessage);

        // GTM tracking for system error
        pushToDataLayer({
          event: 'unsubscribe_form_error',
          error_type: 'system',
          error_message: result.error || 'Unknown error',
        });
      } else {
        // Success
        setSubmitSuccess(true);

        // GTM tracking for success
        pushToDataLayer({
          event: 'unsubscribe_form_success',
          email_domain: email.split('@')[1], // Track domain only, not full email
        });
      }
    } catch (error) {
      console.error('Unsubscribe submission error:', error);
      const errorHeading = getDictionaryValue(
        'Components.EmailUnsubscribe.SystemErrorHeading',
        'System Error'
      );
      const errorSubHeading = getDictionaryValue(
        'Components.EmailUnsubscribe.SystemErrorSubHeading',
        'An error occurred while processing your request. Please try again later.'
      );
      const genericErrorMessage = `${errorHeading} | ${errorSubHeading}`;
      setSubmitError(genericErrorMessage);

      // GTM tracking for unexpected error
      pushToDataLayer({
        event: 'unsubscribe_form_error',
        error_type: 'unexpected',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prepare success message
  const successHeading = getDictionaryValue(
    'Components.EmailUnsubscribe.SuccessHeading',
    'You have been successfully unsubscribed.'
  );
  const successSubHeading = getDictionaryValue(
    'Components.EmailUnsubscribe.SuccessSubHeading',
    'You will no longer receive emails from us.'
  );

  const renderSuccessState = () => {
    return (
      <div className={styles.successContainer} role="alert" aria-live="polite" tabIndex={-1}>
        <p className={styles.successHeading}>{successHeading}</p>
        <p className={styles.successSubHeading}>{successSubHeading}</p>
      </div>
    );
  };
  const emailInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (submitError) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [submitError]);
  // Show form
  return (
    <div className={styles.unsubscribeWrapper}>
      <div className={styles.unsubscribeContent}>
        <div className={styles.formContainer}>
          {!submitSuccess && (
            <div className={styles.header}>
              <h1 className={styles.title}>{formTitle}</h1>
              {formSubheading && <p className={styles.subheading}>{formSubheading}</p>}
            </div>
          )}

          {submitSuccess && renderSuccessState()}

          {submitError && (
            <div className={styles.errorBannerWrapper}>
              <MessageBanner type="error" message={submitError} />
            </div>
          )}

          {!submitSuccess && (
            <>
              <div className={styles.requiredIndicatorWrapper}>
                <span className={styles.requiredIndicator}>{requiredText}</span>
              </div>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.formFields}>
                  <FormField
                    label={emailLabel}
                    labelFor={FORM_FIELD_IDS.EMAIL}
                    error={hasAttemptedSubmit ? getFieldError(FORM_FIELD_IDS.EMAIL) : undefined}
                    showInlineError={true}
                    required
                    errorId={`${FORM_FIELD_IDS.EMAIL}-error`}
                  >
                    <TextInput
                      ref={emailInputRef}
                      errorId={`${FORM_FIELD_IDS.EMAIL}-error`}
                      error={
                        hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.EMAIL) ? true : undefined
                      }
                      aria-required={true}
                      id={FORM_FIELD_IDS.EMAIL}
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleEmailChange}
                      onPaste={handleEmailPaste}
                      placeholder={emailPlaceholder}
                      disabled={isSubmitting}
                      aria-invalid={hasAttemptedSubmit && !!getFieldError(FORM_FIELD_IDS.EMAIL)}
                      aria-describedby={
                        hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.EMAIL)
                          ? `${FORM_FIELD_IDS.EMAIL}-error`
                          : undefined
                      }
                    />
                  </FormField>
                </div>

                <CtaButton
                  ctaStyle="filled"
                  ctaVariant="brand"
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? getDictionaryValue('Components.EmailUnsubscribe.Processing', 'Processing...')
                    : ctaButtonText}
                </CtaButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
